import { useParams } from "react-router"
import { Suspense, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useCogsStore } from "@/feature/financial-model/ecommerce/cogs/store"
import { PageContextProvider } from "@/lib/context/page"
import { PeriodType } from "@/types"
import { useGetDeatiledSingleFinancialModelQuery } from "@/feature/financial-model/api"
import { CogsViewSwitcher } from "@/feature/financial-model/ecommerce/cogs/components/CogsViewSwitcher"
import { CogsTable } from "@/feature/financial-model/ecommerce/cogs/components/direct-costs-tables.tsx"
import { Skeleton } from "@/components/ui/skeleton"
import { EcommerceCommonHeader } from "@/components/shared/headers"
import { ScrollableRouteContainer } from "@/components/shared/Container"
import { PageBody, PageHeader } from "@/components/shared/page-sections.tsx"
import { useSuspenseUnitsSettings } from "@/feature/financial-model/ecommerce/cogs/api"
import { Show } from "@/components/shared/show.tsx"
import { DirectCostsDetailedTables } from "@/feature/financial-model/ecommerce/cogs/components/direct-costs-detailed-tables.tsx"
import { CogsBadge } from "@/feature/financial-model/ecommerce/cogs/components/CogsBadges.tsx"

const CogsDirectCostsPeriodRoute = () => {
  const { view } = useCogsStore()
  const { period } = useParams<{ period: PeriodType }>()

  const defaultState = useMemo(
    () => ({
      view,
      period: period!,
      queryPeriod: period!,
    }),
    [view, period]
  )
  return (
    <PageContextProvider defaultState={defaultState}>
      <CogsPeriods />
    </PageContextProvider>
  )
}

const CogsPeriods = () => {
  const { t } = useTranslation(`cogs`)
  const { id: financialModelId, period } = useParams<{
    id: string
    period: PeriodType
  }>()
  const { financialModel } = useGetDeatiledSingleFinancialModelQuery(
    financialModelId!
  )
  const { data: unitSettings } = useSuspenseUnitsSettings(financialModelId!)
  return (
    <ScrollableRouteContainer className={`p-0`}>
      <PageHeader>
        <EcommerceCommonHeader
          linkText={t(`cogs.home.header.back`)}
          path={`/dashboard/financial-model/${financialModelId}/ecommerce/cogs/${period}-data`}
        />
        <div className="flex justify-between mt-6">
          <div>
            <div className="flex items-center">
              <h1 className="page-title">
                {t(`cogs.periods.direct_costs.title`)}
                {` - `}
                <span className="page-title-secondary-content capitalize">
                  {period}
                </span>
              </h1>
            </div>
            <p className="page-subtitle">
              {`${t(`cogs.home.header.subtitle`)} | ${financialModel?.data?.name}`}
            </p>
            <Show when={unitSettings.level_of_granularity === `detailed`}>
              <div className={`mt-4`}>
                <CogsBadge type={`detailed_feature`} size="md" variant="info" />
              </div>
            </Show>
          </div>
          <CogsViewSwitcher />
        </div>
      </PageHeader>
      <PageBody>
        <Suspense fallback={<Skeleton className="h-[500px] w-full" />}>
          <Show
            when={unitSettings.level_of_granularity === `detailed`}
            fallback={<CogsTable />}
          >
            <DirectCostsDetailedTables />
          </Show>
        </Suspense>
      </PageBody>
    </ScrollableRouteContainer>
  )
}

export default CogsDirectCostsPeriodRoute
